/**
 * Shared push notification TypeScript types and transforms (T109).
 *
 * Used by both desktop and PWA frontends. Backend returns snake_case;
 * these transforms convert to camelCase for frontend consumption.
 */

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export enum NotificationCategory {
  TradeExecution = 'trade_execution',
  AgentCompletion = 'agent_completion',
  SmartNewsBreaking = 'smart_news_breaking',
  SmartNewsCategoryAlert = 'smart_news_category_alert',
  SurfConditions = 'surf_conditions',
}

// ---------------------------------------------------------------------------
// Request types (camelCase frontend -> snake_case backend via service layer)
// ---------------------------------------------------------------------------

export interface PushSubscribeRequest {
  endpoint: string;
  p256dh: string;
  auth: string;
  userAgent?: string;
}

export interface PushUnsubscribeRequest {
  endpoint: string;
}

export interface NotificationPreference {
  category: string;
  enabled: boolean;
}

export interface NotificationPreferencesUpdateRequest {
  preferences: NotificationPreference[];
}

// ---------------------------------------------------------------------------
// Response types (camelCase)
// ---------------------------------------------------------------------------

export interface VapidKeyResponse {
  publicKey: string;
}

export interface PushSubscriptionResponse {
  id: string;
  endpoint: string;
  userAgent: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationPreferencesResponse {
  preferences: NotificationPreference[];
}

export interface UnsubscribeResponse {
  success: boolean;
  message: string;
}

// ---------------------------------------------------------------------------
// Raw backend response types (snake_case)
// ---------------------------------------------------------------------------

interface RawVapidKey {
  public_key?: string;
}

interface RawSubscription {
  id?: string;
  endpoint?: string;
  user_agent?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface RawSubscriptionList {
  subscriptions?: RawSubscription[];
}

interface RawPreferences {
  preferences?: NotificationPreference[];
}

// ---------------------------------------------------------------------------
// Transform functions
// ---------------------------------------------------------------------------

export function transformVapidKey(raw: RawVapidKey): VapidKeyResponse {
  return {
    publicKey: raw.public_key ?? '',
  };
}

export function transformSubscription(raw: RawSubscription): PushSubscriptionResponse {
  return {
    id: raw.id ?? '',
    endpoint: raw.endpoint ?? '',
    userAgent: raw.user_agent ?? null,
    createdAt: raw.created_at ?? '',
    updatedAt: raw.updated_at ?? '',
  };
}

export function transformSubscriptionList(raw: RawSubscriptionList): PushSubscriptionResponse[] {
  const subs = raw.subscriptions ?? [];
  return subs.map(transformSubscription);
}

export function transformPreferences(raw: RawPreferences): NotificationPreference[] {
  return raw.preferences ?? [];
}
